import type { ReactNode } from "react";
import { ArrowRight, Handshake } from "lucide-react";
import { Section } from "./ui/section";
import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button";
import { MoneyAmount } from "./MoneyAmount";
import { useInfiniteSettlements } from "../api/hooks";
import type { Group, Settlement } from "../api/types";

/**
 * Full, paginated history of every settlement in the group — visible to all
 * members, so anyone can see who has paid whom.
 */
export function SettlementsSection({ group }: { group: Group }): ReactNode {
  const q = useInfiniteSettlements(group.id);
  const settlements = q.data?.pages.flatMap((page) => page.items) ?? [];

  const nameOf = (id: string): string =>
    group.participants.find((p) => p.id === id)?.displayName ?? "Someone";

  if (q.isLoading) {
    return (
      <Section title="Payment history">
        <Skeleton className="h-24 rounded-md" />
      </Section>
    );
  }

  if (settlements.length === 0) {
    return (
      <Section title="Payment history">
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
            <Handshake className="size-6" />
          </span>
          <p className="text-body-md text-on-surface-variant">
            No payments recorded yet. Settle up above to log one.
          </p>
        </div>
      </Section>
    );
  }

  return (
    <Section title="Payment history" contentClassName="space-y-stack-sm">
      {settlements.map((s) => (
        <SettlementRow key={s.id} settlement={s} nameOf={nameOf} />
      ))}
      {q.hasNextPage ? (
        <div className="pt-2 text-center">
          <Button
            variant="ghost"
            onClick={() => void q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
          >
            {q.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

function SettlementRow({
  settlement,
  nameOf,
}: {
  settlement: Settlement;
  nameOf: (id: string) => string;
}): ReactNode {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-outline-variant/60 p-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-tertiary-container text-on-tertiary-container">
          <Handshake className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-1.5 truncate text-body-lg">
            <span className="truncate">{nameOf(settlement.payerId)}</span>
            <ArrowRight
              className="size-3.5 shrink-0 text-on-surface-variant"
              aria-label="paid"
            />
            <span className="truncate">{nameOf(settlement.payeeId)}</span>
          </p>
          <p className="text-label-md text-on-surface-variant">
            {new Date(settlement.createdAt).toLocaleDateString()}
            {settlement.note ? ` · ${settlement.note}` : ""}
          </p>
        </div>
      </div>
      <MoneyAmount
        minor={settlement.amount}
        currency={settlement.currency}
        className="shrink-0 text-headline-md"
      />
    </div>
  );
}
